import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoCicloFacturacion, EnumTipoFacturacion, EnumTipoSujeto } from '../../../../api'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import {
  ExecutionCycleFormDataModel,
  ExecutionCycleFormDialog
} from '../../../../components/shared/executionCycleFormDialog'
import SimpleDialog from '../../../../components/shared/simpleDialog/simpleDialog'

interface CloseExecutionCycleDialogProps {
  open: boolean
  hasOpenSettlements?: boolean
  cycleName?: string
  executionCycleId: number
  invoiceCycleId: number
  invoiceCycleType?: EnumTipoCicloFacturacion
  subjectType?: EnumTipoSujeto
  billingType?: EnumTipoFacturacion
  onClose: () => void
  onCloseWithoutInvoiceGeneration: () => void
  onCloseWithInvoiceGeneration: (
    startDate: string,
    endDate: string,
    reInvoiceType?: number,
    groupingType?: number
  ) => void
}

/**
 * Diálogo para cerrar el ciclo de ejecución con dos opciones:
 * 1. Cerrar y generar facturas (abre formulario para seleccionar fechas)
 * 2. Cerrar sin generar facturas (cierre directo sin formulario)
 */
export const CloseExecutionCycleDialog: React.FC<CloseExecutionCycleDialogProps> = ({
  open,
  hasOpenSettlements = false,
  cycleName,
  executionCycleId,
  invoiceCycleId,
  invoiceCycleType,
  subjectType,
  billingType,
  onClose,
  onCloseWithoutInvoiceGeneration,
  onCloseWithInvoiceGeneration
}) => {
  const [t] = useTranslation(['settlementsInvoices'])
  const [showDateForm, setShowDateForm] = useState(false)

  const handleCloseWithInvoices = () => {
    // Abrir formulario para seleccionar fechas
    setShowDateForm(true)
  }

  const handleCloseWithoutInvoices = () => {
    // Cerrar ciclo SIN reciclar facturas
    onCloseWithoutInvoiceGeneration()
  }

  const handleDateFormClose = (formData?: ExecutionCycleFormDataModel) => {
    if (formData) {
      const { startDate, endDate, reInvoiceType, groupingType } = formData
      // Cerrar ciclo CON generación de facturas usando las fechas del formulario
      onCloseWithInvoiceGeneration(startDate!, endDate!, reInvoiceType, groupingType)
    }

    setShowDateForm(false)
    onClose()
  }

  const footerActions: CardActionProps[] = [
    {
      label: t('settlementsInvoices:components.closeExecutionCycleDialog.actions.cancel'),
      handleActionClick: onClose,
      variant: 'text',
      color: 'secondary'
    },
    {
      label: t(
        'settlementsInvoices:components.closeExecutionCycleDialog.actions.closeWithoutInvoiceGeneration'
      ),
      handleActionClick: handleCloseWithoutInvoices,
      variant: 'text',
      color: 'primary'
    },
    {
      label: t(
        'settlementsInvoices:components.closeExecutionCycleDialog.actions.closeWithInvoiceGeneration'
      ),
      handleActionClick: handleCloseWithInvoices,
      variant: 'text',
      color: 'primary',
      visibleIf: hasOpenSettlements
    }
  ]

  // Memoize to avoid creating new objects on every render
  const memoizedInitialData = useMemo(
    () => ({
      cycleName: cycleName || 'Ciclo de ejecución'
    }),
    [cycleName]
  )

  const memoizedCloseExecutionCycleData = useMemo(
    () => ({
      executionCycleId,
      invoiceCycleId,
      invoiceCycleType,
      subjectType,
      billingType,
      generateInvoices: true
    }),
    [executionCycleId, invoiceCycleId, invoiceCycleType, subjectType, billingType]
  )

  //
  return (
    <>
      <SimpleDialog
        open={open && !showDateForm}
        maxWidth="sm"
        handleClose={onClose}
        title={t('settlementsInvoices:components.closeExecutionCycleDialog.title')}
        footerActions={footerActions}
        formStyle="confirmationDialog"
      >
        {hasOpenSettlements
          ? t('settlementsInvoices:components.closeExecutionCycleDialog.descWithOpenSettlements')
          : t(
              'settlementsInvoices:components.closeExecutionCycleDialog.descWithoutOpenSettlements'
            )}
      </SimpleDialog>

      <ExecutionCycleFormDialog
        open={showDateForm}
        mode="close"
        initialData={memoizedInitialData}
        closeExecutionCycleData={memoizedCloseExecutionCycleData}
        onClose={handleDateFormClose}
      />
    </>
  )
}
